import { actions, selectors } from 'data'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { refreshToken } from 'services/api/auth'
import { current } from 'services/api/user'
import { setUser } from 'utils/datadog'
import {
  decodeJwt,
  getJWTToken,
  removeJWTToken,
  saveCSRFToken,
  setExpirationTime,
  setJWTToken
} from 'utils/tokenHelper'

// Check if user is authed. Use cases:
// - no session
// - authed with jwt in localstorage
export default function useAuth() {
  const dispatch = useDispatch()
  const jwtStr = getJWTToken()
  const [done, setDone] = useState(false)
  const config = useSelector(selectors.config.getConfig)

  useEffect(() => {
    dispatch(actions.auth.isAuthing(true))

    if (jwtStr) {
      const jwtPayload = decodeJwt(jwtStr)
      if (jwtPayload) {
        setExpirationTime(String(jwtPayload.exp * 1000))
      } else {
        removeJWTToken()
      }
    } else {
      removeJWTToken()
    }

    performance.mark('RefreshToken:start')
    refreshToken(true)
      .then((data) => {
        performance.mark('RefreshToken:end')
        performance.measure('RefreshToken', 'RefreshToken:start', 'RefreshToken:end')
        if (!data.ok) {
          removeJWTToken()
          dispatch(actions.auth.isAuthing(false))
          setDone(true)
          return
        }

        setJWTToken(data.response.token!)
        setExpirationTime(data.response.sessionExpirationTime.toString())
        saveCSRFToken(data.response.csrfToken)
        return current()
      })
      .then((res) => {
        if (!res || !res.ok) {
          return
        }
        dispatch(actions.auth.checkSession())
        dispatch(actions.auth.isAuthing(false))
        setDone(true)
        const user = res.response
        dispatch(actions.auth.setUser({ user }))

        const unified = !!user.unifiedAccountWalletGuid
        // hardcoded to match 'sm' theme width
        const isMobile = window.innerWidth <= 768
        dispatch(
          actions.analytics.trackEvent({
            key: 'Signed In',
            properties: {
              authentication_type: 'PASSWORD',
              unified,
              web_device: isMobile ? 'MOBILE' : 'DESKTOP'
            }
          })
        )

        if (config.datadog) {
          setUser({
            email: user.email,
            id: user.id,
            name: `${user.firstName} ${user.lastName}`
          })
        }
      })
      .catch(() => {
        // catch all
        removeJWTToken()
        dispatch(actions.auth.isAuthing(false))
        setDone(true)
      })
  }, [])

  return done
}
